import { useState, useCallback } from "react";
import { STANDE, STANDE_DARK, GREY, GREY_DARK, ON_TINT } from "./standeTokens";

// Shared light/dark toggle for the classroom. The preference is persisted so it
// stays consistent across the dashboards and the inside-classroom workspace.
export function useClassroomColorMode() {
  const [isDark, setIsDark] = useState(() => {
    // Default to dark (to match stande.co); respect a saved preference.
    const saved = localStorage.getItem("classroomColorMode");
    return saved ? saved === "dark" : true;
  });
  const toggleColorMode = useCallback(() => {
    setIsDark((prev) => {
      const next = !prev;
      localStorage.setItem("classroomColorMode", next ? "dark" : "light");
      return next;
    });
  }, []);
  return { isDark, toggleColorMode };
}

// The palette mirrors stande.co (stande-landing globals.css) so the classroom
// reads as the same product: cream/white light mode, purple-tinted near-black
// dark mode, and the landing brand red-orange as the accent.
//
// PALETTES holds literal values. Chakra modals/menus render in a portal on
// <body>, outside the .stande-classroom wrapper, where the CSS variables do not
// resolve — portal components must use PALETTES[mode] directly.
export const PALETTES = {
  light: {
    page: STANDE.cream,
    // Assignment card (Figma): cream card with a white status pill on it.
    cardBg: STANDE.cream,
    cardPill: "#ffffff",
    cardTitle: GREY[800],
    surface: "#ffffff",
    surfaceSubtle: STANDE.cream,
    border: GREY[200],
    text: STANDE.black,
    textSub: GREY[600],
    textMuted: GREY[400],
    hover: STANDE.grey,
    accent: STANDE.secondaryOrange,
    accentHover: "#e63d00",
    accentSoft: STANDE.lightRed,
    accentSoftText: ON_TINT.light.lightRed,
    onAccent: "#ffffff",
    info: STANDE.primaryBlue,
    infoSoft: "#e4f9ff",
    infoText: ON_TINT.light.primaryBlue,
    inputBg: "#ffffff",
    inputBorder: GREY[200],
    solidBg: STANDE.black,
    solidText: "#ffffff",
    track: STANDE.grey,
    overlay: "rgba(34,34,34,0.45)",
    // Chrome floating over the canvas: stande-cream, see-through, blurred
    // behind, so what is on the board is softened rather than hidden.
    glass: "rgba(251,251,251,0.72)",
    glassEdge: "rgba(255,255,255,0.7)",
  },
  dark: {
    page: GREY_DARK.page,
    cardBg: GREY_DARK.subtle,
    cardPill: "#2e2e2e",
    cardTitle: GREY_DARK.text,
    surface: GREY_DARK.surface,
    surfaceSubtle: GREY_DARK.subtle,
    border: GREY_DARK.border,
    text: GREY_DARK.text,
    textSub: GREY_DARK.textSub,
    textMuted: GREY_DARK.textMuted,
    hover: GREY_DARK.hover,
    accent: STANDE_DARK.secondaryOrange,
    accentHover: "#ff8563",
    accentSoft: STANDE_DARK.lightRed,
    accentSoftText: ON_TINT.dark.lightRed,
    onAccent: "#ffffff",
    info: STANDE_DARK.primaryBlue,
    infoSoft: "#0d2b36",
    infoText: ON_TINT.dark.primaryBlue,
    inputBg: "#141414",
    inputBorder: "#363636",
    solidBg: GREY_DARK.text,
    solidText: STANDE.black,
    track: GREY_DARK.border,
    overlay: "rgba(0,0,0,0.6)",
    glass: "rgba(26,26,26,0.68)",
    glassEdge: "rgba(255,255,255,0.08)",
  },
};

// Classroom color tokens. Each value is a CSS variable with the LIGHT value as
// its fallback. Inside a `.stande-classroom[data-theme="dark"]` wrapper the
// variables are defined (see classroom-theme.css) and everything flips to dark.
const L = PALETTES.light;

export const c = {
  page: `var(--cl-page, ${L.page})`,
  cardBg: `var(--cl-card-bg, ${L.cardBg})`,
  cardPill: `var(--cl-card-pill, ${L.cardPill})`,
  cardTitle: `var(--cl-card-title, ${L.cardTitle})`,
  surface: `var(--cl-surface, ${L.surface})`,
  surfaceSubtle: `var(--cl-surface-subtle, ${L.surfaceSubtle})`,
  border: `var(--cl-border, ${L.border})`,
  borderSoft: `var(--cl-border, ${L.border})`,

  text: `var(--cl-text, ${L.text})`,
  textSub: `var(--cl-text-sub, ${L.textSub})`,
  textMuted: `var(--cl-text-muted, ${L.textMuted})`,

  hover: `var(--cl-hover, ${L.hover})`,

  accent: `var(--cl-accent, ${L.accent})`,
  accentHover: `var(--cl-accent-hover, ${L.accentHover})`,
  accentSoft: `var(--cl-accent-soft, ${L.accentSoft})`,
  accentSoftText: `var(--cl-accent-soft-text, ${L.accentSoftText})`,
  onAccent: `var(--cl-on-accent, ${L.onAccent})`,

  info: `var(--cl-info, ${L.info})`,
  infoSoft: `var(--cl-info-soft, ${L.infoSoft})`,
  infoText: `var(--cl-info-text, ${L.infoText})`,

  inputBg: `var(--cl-input-bg, ${L.inputBg})`,
  inputBorder: `var(--cl-input-border, ${L.inputBorder})`,

  // Neutral "solid" surfaces (dark buttons, arrow circles): light = ink on
  // white; dark = light chip on dark so it still reads.
  solidBg: `var(--cl-solid-bg, ${L.solidBg})`,
  solidText: `var(--cl-solid-text, ${L.solidText})`,

  track: `var(--cl-track, ${L.track})`,

  glass: `var(--cl-glass, ${L.glass})`,
  glassEdge: `var(--cl-glass-edge, ${L.glassEdge})`,
};

/**
 * The glass every piece of chrome over the canvas shares: the cards, the
 * header, the canvas controls. Spread onto a Box.
 */
export const popupSurface = {
  // A pop-up is a card in front of the room, not part of it: solid
  // stande-cream, so what it says is never read through the board behind.
  // Glass is for chrome that lives on the canvas (the dock, the controls).
  bg: c.page,
  border: "1px solid",
  borderColor: c.borderSoft,
  boxShadow: "0 24px 64px -24px rgba(34,34,34,0.35)",
};

export const glassSurface = {
  bg: c.glass,
  backdropFilter: "blur(18px) saturate(1.4)",
  border: "1px solid",
  borderColor: c.glassEdge,
  boxShadow: "0 1px 0 rgba(255,255,255,0.5) inset, 0 12px 32px -18px rgba(34,34,34,0.32)",
};
